import { ClientTag, SQLGeneratedProcess } from "@avenueops/shared-types";
import { Role } from "@prisma/client";
import * as HoverCard from "@radix-ui/react-hover-card";
import AddTagButton from "components/tags/AddTagButton";
import TagPill from "components/tags/TagPill";
import { useMembership } from "context/OrganizationContext";
import { trpc, trpc2 } from "utils/trpc";

interface Props {
  row: SQLGeneratedProcess;
  viewOnly?: boolean;
}

export function Tags({ row, viewOnly }: Props): JSX.Element | null {
  // Get the user's role
  const membership = useMembership();
  const role = membership?.role;
  const optionsFetchEnabled = role && role !== Role.GUEST;

  if (!row.tagsArray || row.tagsArray.length === 0) {
    return (
      <div
        className=" flex flex-row w-fit"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {viewOnly ? null : <AddTagButton workflowId={row.workflowId} parentObject={{ type: "PROCESS", ids: [row.id] }} currentTags={[]} size="SMALL" />}
      </div>
    );
  }
  const utils = trpc.useContext();
  const utils2 = trpc2.useContext();
  const onRemoveSuccess = async () => {
    await Promise.allSettled([
      utils.tags.getOrgTagsAndTaggedProcessCounts.invalidate(),
      utils2.v2.workflows.get.invalidate(),
      utils.processes.get.invalidate(),
      utils.tags.getProcessTags.invalidate(),
    ]);
  };
  const { mutateAsync: removeTagFromProcess } = trpc.tags.removeTagFromProcess.useMutation({ onSuccess: onRemoveSuccess });

  const removeTag = async (tagId: string) => {
    await removeTagFromProcess({ processId: row.id, tagId });
  };

  const { data: workflow } = trpc2.v2.workflows.get.useQuery({ workflowId: row.workflowId }, { enabled: !viewOnly && optionsFetchEnabled });
  const dynamicTags = workflow?.dynamicTags;

  return (
    <div>
      <HoverCard.Root>
        <HoverCard.Trigger asChild>
          <div className="flex flex-row whitespace-nowrap overflow-scroll items-center gap-2">
            <TagPill
              workflowId={row.workflowId}
              name={`${row.tagsArray[0].name} ${row.tagsArray.length > 1 ? `(+${row.tagsArray.length - 1}) ` : ""}`}
              tag={{ ...row.tagsArray[0], dynamicTags: dynamicTags ?? [] }}
              clickable={false}
              removeTag={viewOnly ? undefined : removeTag}
              size="SMALL"
            />
          </div>
        </HoverCard.Trigger>
        <HoverCard.Portal>
          <HoverCard.Content className="relative z-15 shadow-lg max-w-md  border rounded-lg p-3 bg-white" sideOffset={5} avoidCollisions={true}>
            <div
              className="flex flex-col gap-2 overflow-visible"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {row.tagsArray.map((tag: Pick<ClientTag, "id" | "name" | "color">, index) => {
                return (
                  <TagPill
                    key={index}
                    workflowId={row.workflowId}
                    name={tag.name}
                    tag={{ id: tag.id, color: tag.color, dynamicTags: dynamicTags ?? [] }}
                    clickable={false}
                    removeTag={viewOnly ? undefined : removeTag}
                    size="SMALL"
                  />
                );
              })}
              {viewOnly ? null : (
                <AddTagButton
                  workflowId={row.workflowId}
                  parentObject={{ type: "PROCESS", ids: [row.id] }}
                  currentTags={(row.tagsArray as Pick<ClientTag, "id" | "name" | "color">[]) ?? []}
                  size="SMALL"
                />
              )}
            </div>
          </HoverCard.Content>
        </HoverCard.Portal>
      </HoverCard.Root>
    </div>
  );
}
